import React, { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon as MenuListItemIcon,
  MenuItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Reply as ReplyIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { MODERATION_REASONS, REASON_MAP } from 'components/collab/calls/comments/moderationReasons';
import LikeButton from 'components/common/buttons/LikeButton';
import EmojiReactionButton from 'components/common/buttons/EmojiReactionButton';

import {SuggestButton, InviteButton, MeetButton, MessageButton} from 'components/social/profile/ActionButtons';

import MeetingScheduleDialog from '../common/dialogs/MeetingScheduleDialog';
import InviteToConsortiumDialog from "../common/dialogs/InviteToConsortiumDialog";
import SuggestEntityDialog from "../common/dialogs/SuggestEntityDialog";


import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import EntityLink from './links/EntityLink';
import { formatDistanceToNow } from 'date-fns';
import { MentionText } from 'components/social/feed/mentions';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import InterestComposer, { InterestReplyForm } from 'components/collab/interests/InterestComposer';
import { PostShareButton, RepostButton } from 'components/common/PostShare';
import PostStatsButton from 'components/common/PostStats';
import { postMarkdown } from 'shared/helpers/postText';

/**
 * Single Comment Display with nested replies support
 */
function CommentItem({
  comment,
  currentUser,
  onDelete,
  onReply,
  onWarn,
  onUnflag,
  onRefresh,
  targetType,
  targetId,
  canModerate = false,
  showReplyButton = true,
  depth = 0,
  maxDepth = 2,
}) {
  const navigate = useNavigate();
  const theme = useTheme();

  const isAuthor = String(comment.author) === String(currentUser?.id);

  const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // flagged-comment expand toggle
  const [flagExpanded, setFlagExpanded] = useState(false);

  // moderation menu / warn / report
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [warnReason, setWarnReason] = useState('');
  const [warnSaving, setWarnSaving] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportSaving, setReportSaving] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  const canReport = Boolean(currentUser) && !isAuthor && !canModerate;
  // on an interest (a call's top-level comment) reporting is a button in its actions, so the menu is for moderators
  const showMenu = canModerate || (canReport && !(targetType === 'call' && depth === 0));

  // the author edits an interest in place, in the composer it was written in
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const saveEdit = async (body, typeOfInterest) => {
    setEditError(null);
    try {
      await axiosService.patch(`/interact/comments/${comment.id}/`, { body, type_of_interest: typeOfInterest });
    } catch (err) {
      setEditError(err.response?.data?.body?.[0] || 'Your changes could not be saved. Please try again.');
      throw err;           // the composer keeps them
    }
    setEditing(false);
    onRefresh?.();
  };

  const handleMessage = () => navigate(`/messages/new/?user=${comment.author}`);

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      await onReply(comment.id, replyText.trim());
      setReplyText('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Failed to submit reply:', error);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleWarnSubmit = async () => {
    if (!warnReason) return;
    const reason = REASON_MAP[warnReason];
    setWarnSaving(true);
    try {
      await onWarn(comment.id, reason?.detail || reason?.label || warnReason);
      setWarnDialogOpen(false);
      setWarnReason('');
    } finally {
      setWarnSaving(false);
    }
  };

  const handleReportSubmit = async () => {
    if (!reportReason) return;
    setReportSaving(true);
    try {
      await axiosService.post(`/interact/comments/${comment.id}/report/`, { reason_key: reportReason });
      setReportDone(true);
      setReportReason('');
    } finally {
      setReportSaving(false);
    }
  };


  return (
    <Box 
      id={`comment-${comment.id}`}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1.5, 
        borderRadius: 1,
        p: depth > 0 ? 1 : 1.5,
        ml: targetType === 'call' ? 0 : 1,
        pl: 2,
        width: '100%',
        maxWidth: '100%',
        scrollMarginTop: '80px',
        background: '#fff',
        boxShadow: depth > 0 ? theme.shadows[1] : theme.shadows[2],
        mb: depth > 0 ? 1.5 : 0,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>



        
      <EntityLink 
        type="user" 
        id={comment.author_info?.id}
        showAvatar={true}
        avatar={{
          src: comment.author_info?.avatar,
          alt: comment.author_info?.first_name,
          children: comment.author_info?.first_name?.[0],
          sx: {
            width: depth > 0 ? 28 : 32, 
            height: depth > 0 ? 28 : 32, 
            mr: 1.5, 
            fontSize: depth > 0 ? '0.9rem' : '1.5rem',
            backgroundColor: theme.palette.primary.avatar,
            color: 'white',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
            mb: 0.5,
            mt: 0.5,
          }
        }}
      />

          <EntityLink
            type="user"
            id={comment.author_info?.id}
            name={comment.author_info ? 
              `${comment.author_info.first_name} ${comment.author_info.last_name}` : 
              'Anonymous User'
            }
            variant="subtitle2"
            underline="hover"
          />
          <Typography variant="caption" color="text.secondary">
             {formatDistanceToNow(new Date(comment.created), { addSuffix: true })}
          </Typography>
          {targetType === 'call' && depth === 0 && (   // replies have no type of interest
            <Chip
              size="small"
              label={
                comment.type_of_interest === 0 ? 'Seeking for a Consortium' :
                comment.type_of_interest === 1 ? 'Seeking for Partners' :
                'Comment'
              }
              color="primary"
              variant="outlined"
            />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isAuthor && targetType === 'call' && depth === 0 && !editing && (
              <Tooltip title="Edit">
                <IconButton size="small" aria-label="Edit this interest" onClick={() => setEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isAuthor && onDelete && (
              <IconButton size="small" onClick={() => onDelete(comment.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            {showMenu && (
              <>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                >
                  {canModerate ? (
                    comment.is_flagged ? (
                      <MenuItem onClick={() => { setMenuAnchor(null); onUnflag(comment.id); }}>
                        <MenuListItemIcon><FlagOutlinedIcon fontSize="small" /></MenuListItemIcon>
                        Remove warning
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={() => { setMenuAnchor(null); setWarnDialogOpen(true); }}>
                        <MenuListItemIcon><FlagIcon fontSize="small" color="warning" /></MenuListItemIcon>
                        Warn commenter
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem onClick={() => { setMenuAnchor(null); setReportDialogOpen(true); }}>
                      <MenuListItemIcon><FlagOutlinedIcon fontSize="small" color="error" /></MenuListItemIcon>
                      Report comment
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Flagged banner */}
        {comment.is_flagged && (
          <Alert
            severity="warning"
            icon={<FlagIcon fontSize="small" />}
            action={
              <Button size="small" onClick={() => setFlagExpanded((v) => !v)} endIcon={<ExpandMoreIcon sx={{ transform: flagExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}>
                {flagExpanded ? 'Hide' : 'Show'}
              </Button>
            }
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption" fontWeight={600}>
              This comment has been flagged by a moderator.
            </Typography>
            {comment.moderator_note && (
              <Typography variant="caption" display="block" color="text.secondary">
                {comment.moderator_note}
              </Typography>
            )}
          </Alert>
        )}

        <Collapse in={!comment.is_flagged || flagExpanded}>
        {editing && editError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setEditError(null)}>{editError}</Alert>}
        {editing ? (
          <InterestComposer currentUser={currentUser} interest={comment} onCancel={() => { setEditing(false); setEditError(null); }}
            onSubmit={saveEdit} />
        ) : (
        <Typography variant="body1" component="div" sx={{ mt: 1, mr: 2,
                    wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {targetType === 'call' && depth === 0
            ? <Box sx={{ '& p': { mt: 0, mb: 0.75 }, '& p:last-child': { mb: 0 } }}>
                <LabMarkdown images hashtags>{postMarkdown(comment.body)}</LabMarkdown>
              </Box>
            : <MentionText text={comment.body} />}
        </Typography>
        )}

        {/* Like button for comment */}
        <Box sx={{ mt: 0.5 }}>

       {  targetType === 'call' ?

    

<Box display="flex" alignItems="center" flexWrap="wrap" gap={.01} mt={1} ml={-1}>

          {depth === 0 ? (
            <>
              {/* an interest: the same reactions, reply, repost, share (and stats for its author) as posts */}
              <EmojiReactionButton contentType="comment" objectId={comment.id} currentUser={currentUser} size="small" />
              {currentUser && (
                <Button size="small" startIcon={<ReplyIcon fontSize="small" />} onClick={() => setShowReplyForm((v) => !v)}
                  sx={{ color: 'text.secondary', textTransform: 'none', minWidth: 0, '&:hover': { color: 'primary.main' } }}>
                  Reply
                </Button>
              )}
              {currentUser && <RepostButton post={comment} interest />}
              <PostShareButton post={comment} interest withLabel />
              {isAuthor && <PostStatsButton post={comment} interest />}
            </>
          ) : (
            <LikeButton
              contentType="comment"
              contentId={comment.id}
              currentUser={currentUser}
              size="medium"
            />
          )}



{comment?.author !== currentUser?.id && (
  <>
    {comment?.type_of_interest === 0 && (
      <>
        <SuggestButton 
          onClick={() => setSuggestDialogOpen(true)} 
          invertColors={true} 
          fontSize="1.5rem" 
        />
        <InviteButton 
          onClick={() => setInviteToConsortiumDialogOpen(true)} 
          invertColors={true} 
          fontSize="1.5rem" 
        />
      </>
    )}
    
    <MeetButton 
      onClick={() => setMeetingDialogOpen(true)} 
      invertColors={true} 
      fontSize="1.5rem" 
    />
    <MessageButton 
      onClick={handleMessage} 
      invertColors={true} 
      fontSize="1.5rem" 
    />
  </>
)}

{depth === 0 && canReport && (
  <>
    <Box flex={1} />
    <Tooltip title="Report this interest">
      <IconButton size="small" aria-label="Report this interest" onClick={() => setReportDialogOpen(true)}
        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
        <FlagOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </>
)}


</Box>

     

                


          : 
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt:1 }}>
            <EmojiReactionButton 
              contentType={targetType} 
              objectId={targetId} 
              currentUser={currentUser} 
              size="small"
            />
            
            {/* Reply button for post/reflection comments */}
            {targetType !== 'call' && showReplyButton && currentUser && depth <= maxDepth && (
              <Button
                size="small"
                startIcon={<ReplyIcon />}
                onClick={() => setShowReplyForm(!showReplyForm)}
                sx={{ 
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {showReplyForm ? 'Cancel' : 'Reply'}
              </Button>
            )}
          </Box>
          }

        </Box>

        {/* Reply to an interest: plain text with @mentions, as on the Interests page */}
        {showReplyForm && targetType === 'call' && depth === 0 && (
          <InterestReplyForm parentId={comment.id} onCancel={() => setShowReplyForm(false)}
            onSubmitted={() => { setShowReplyForm(false); onRefresh?.(); }} />
        )}

        {/* Reply form */}
        {showReplyForm && targetType !== 'call' && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Avatar 
              src={currentUser?.avatar} 
              alt={currentUser?.first_name || 'User'}
              sx={{ 
                width: 28, 
                height: 28, 
                fontSize: '1rem',
                backgroundColor: theme.palette.primary.avatar,
                color: 'white',
              }}
            >
              {currentUser?.first_name?.[0]}
            </Avatar>
            <TextField
              fullWidth
              size="small"
              placeholder="Write a reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              multiline
              maxRows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReplySubmit();
                }
              }}
              sx={{ backgroundColor: '#fff', borderRadius: 1 }}
            />
            <IconButton 
              color="primary" 
              onClick={handleReplySubmit}
              disabled={!replyText.trim() || submittingReply}
              size="small"
            >
              {submittingReply ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        )}

        {/* Display replies */}
        {comment.replies && comment.replies.length > 0 && (
          <Box 
            sx={{ 
              mt: 2, 
              ml: depth <= maxDepth ? 1 : 0, 
              pl: depth <= maxDepth ? 0.5 : 0,
              borderLeft: depth <= maxDepth ? 1 : 0, 
              borderColor: 'grey.200',
            }}
          >
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                currentUser={currentUser}
                onDelete={onDelete}
                onReply={onReply}
                onWarn={onWarn}
                onUnflag={onUnflag}
                canModerate={canModerate}
                targetType={targetType}
                targetId={targetId}
                showReplyButton={depth <= maxDepth}
                depth={depth + 1}
                maxDepth={maxDepth}
              />
            ))}
          </Box>
        )}

        {/* Show count if max depth reached and there are more replies */}
        {depth >= maxDepth && comment.replies_count > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {comment.replies_count} {comment.replies_count === 1 ? 'reply' : 'replies'}
            </Typography>
          </Box>
        )}
        </Collapse>
      </Box>

      {/* Report dialog (regular users) */}
      <Dialog open={reportDialogOpen} onClose={() => { setReportDialogOpen(false); setReportReason(''); setReportDone(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagOutlinedIcon color="error" fontSize="small" />
          Report comment
        </DialogTitle>
        <DialogContent>
          {reportDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Thank you for your report. A moderator will review this comment.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the reason this comment does not follow the posting guidelines. Your report will be sent to a moderator for review.
              </Typography>
              <FormControl fullWidth required>
                <InputLabel>Reason</InputLabel>
                <Select
                  value={reportReason}
                  label="Reason"
                  onChange={(e) => setReportReason(e.target.value)}
                >
                  {MODERATION_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {reportReason && (
                <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>
                    Why this matters:
                  </Typography>
                  <Typography variant="body2">
                    {REASON_MAP[reportReason]?.detail}
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReportDialogOpen(false); setReportReason(''); setReportDone(false); }}>
            {reportDone ? 'Close' : 'Cancel'}
          </Button>
          {!reportDone && (
            <Button
              variant="contained"
              color="error"
              onClick={handleReportSubmit}
              disabled={reportSaving || !reportReason}
            >
              {reportSaving ? <CircularProgress size={18} /> : 'Submit report'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Warn dialog */}
      <Dialog open={warnDialogOpen} onClose={() => { setWarnDialogOpen(false); setWarnReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagIcon color="warning" fontSize="small" />
          Flag comment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the reason this comment does not meet posting guidelines. The commenter will be notified with the full explanation and their comment will be collapsed.
          </Typography>
          <FormControl fullWidth required>
            <InputLabel>Reason</InputLabel>
            <Select
              value={warnReason}
              label="Reason"
              onChange={(e) => setWarnReason(e.target.value)}
            >
              {MODERATION_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {warnReason && (
            <Alert severity="info" sx={{ mt: 2 }} icon={false}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 0.5 }}>
                Message the commenter will receive:
              </Typography>
              <Typography variant="body2">
                {REASON_MAP[warnReason]?.detail}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWarnDialogOpen(false); setWarnReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleWarnSubmit}
            disabled={warnSaving || !warnReason}
          >
            {warnSaving ? <CircularProgress size={18} /> : 'Send warning'}
          </Button>
        </DialogActions>
      </Dialog>

<MeetingScheduleDialog 
              open={meetingDialogOpen} 
              onClose={() => setMeetingDialogOpen(false)} 
              call={targetId}
              restrictToUsers={currentUser}
          />


{/* Invite to Consortium Dialog */}
      <InviteToConsortiumDialog     
        open={inviteToConsortiumDialogOpen}
        onClose={() => setInviteToConsortiumDialogOpen(false)}
        user={currentUser}
      />


    {/* Suggest Entity Dialog */}
      <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={() => setSuggestDialogOpen(false)}
        user={currentUser}
      />

      {/* <InviteToSuggestEntityDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          entityType="consortium"
          entity={consortium}
          currentUserId={currentUserId}
          mutate={mutate}
          mutateUser={mutateUser}
          mode="invite" // or "suggest"
        /> */}

    </Box>
  );
}


/**
 * Comments Section for a Reflection
 */
export function Comments({ targetId, targetType, currentUser, onCommentCountChange, canModerate = false, composerRef = null }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const visibleItems = comments.slice(0, 2);
  const collapsedItems = comments.slice(2);
  const theme = useTheme();

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axiosService.get('/interact/comments/', {
        params: {
          target_type: targetType,
          target_id: targetId,
          include_replies: true,  // Include nested replies
        }
      });
      const fetchedComments = response.data.results || response.data || [];
      setComments(fetchedComments);
      // Notify parent of comment count
      if (onCommentCountChange) {
        onCommentCountChange(fetchedComments.length);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch comment count on mount
  useEffect(() => {
    fetchComments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  // Refetch when expanded (for latest data)
  useEffect(() => {
    if (expanded) {
      fetchComments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Scroll to comment if hash is present in URL
  useEffect(() => {
    if (comments.length > 0 && window.location.hash) {
      const hash = window.location.hash;
      if (hash.startsWith('#comment-')) {
        // Auto-expand comments section
        setExpanded(true);
        // Wait for DOM update and scroll
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight effect
            element.style.transition = 'background-color 0.5s';
            element.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
            setTimeout(() => {
              element.style.backgroundColor = 'transparent';
            }, 2000);
          }
        }, 100);
      }
    }
  }, [comments]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await axiosService.post('/interact/comments/', {
        target_type: targetType,
        target_id: targetId,
        body: newComment.trim(),
      });
      setNewComment('');
      await fetchComments();
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setError('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // an interest on a call, from InterestComposer (it keeps the draft when this throws)
  const handleSubmitInterest = async (body, typeOfInterest) => {
    setError(null);
    try {
      await axiosService.post('/interact/comments/', {
        target_type: targetType, target_id: targetId, body, type_of_interest: typeOfInterest,
      });
    } catch (err) {
      setError(err.response?.data?.body?.[0] || 'Failed to post your interest. Please try again.');
      throw err;
    }
    await fetchComments();
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axiosService.delete(`/interact/comments/${commentId}/`);
      await fetchComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleReply = async (parentCommentId, replyText) => {
    try {
      await axiosService.post('/interact/comments/', {
        target_type: targetType,
        target_id: targetId,
        body: replyText,
        parent: parentCommentId,
      });
      await fetchComments();
    } catch (err) {
      console.error('Failed to submit reply:', err);
      throw err;
    }
  };

  const handleWarnComment = async (commentId, note) => {
    await axiosService.post(`/interact/comments/${commentId}/warn/`, { note });
    await fetchComments();
  };

  const handleUnflagComment = async (commentId) => {
    await axiosService.post(`/interact/comments/${commentId}/unflag/`);
    await fetchComments();
  };

  return (
    


    <Box
      sx={{
        width: '100%',   // reflections: the column of the reflection's page sets the width
        pt: 1,
        pb: 1,
      }}
    >


         {/* Add an interest: written like a post (markdown, mentions) */}
         {currentUser && targetType === 'call' && (
           <InterestComposer currentUser={currentUser} composerRef={composerRef} onSubmit={handleSubmitInterest} />
         )}
         {error && targetType === 'call' && (
           <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>
         )}

         {/* Add comment form */}
         {currentUser && targetType !== 'call' && (
           <Box
             ref={composerRef}
             sx={{
               display: 'flex',
               flexDirection: { xs: 'column', sm: 'row' },
               gap: 1,
               mt: 2,
               mb: 2,
               alignItems: 'flex-start',
               width: targetType !== 'call' ? 'calc(100% - 8px)' : '100%',   // the left margin stays inside the post
               ml: targetType !== 'call' ? 1 : 0,
             }}
           >
             <Avatar 
               src={currentUser?.avatar} 
               alt={currentUser?.first_name || 'User'}
               sx={{ width: 32, height: 32, fontSize: '1.5rem', flexShrink: 0,   // the row's gap spaces it
                backgroundColor: theme.palette.primary.avatar,
                color: 'white',
                // boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                // border: `1px solid ${theme.palette.sidebar.active}`,
                // mb: 0.5,
                // mt: 0.5
               }}
             >
               {currentUser.first_name?.[0]}
             </Avatar>
             <TextField
               fullWidth
               sx={{ flex: 1, minWidth: 0, backgroundColor: '#fff', 
                borderRadius: 1, 
                boxShadow: theme.shadows[1] }}
               size="small"
               placeholder="Write a comment..."
               value={newComment}
               onChange={(e) => setNewComment(e.target.value)}
               multiline
               maxRows={5}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && !e.shiftKey) {
                   e.preventDefault();
                   handleSubmitComment();
                 }
               }}
             />

           
<Box 
  display="flex" 
  alignItems="center" 
  gap={1} 
  sx={{ 
    flexWrap: 'nowrap',  // Changed from 'wrap' to 'nowrap'
    // only as wide as the send button: the comment box takes the rest
    width: { xs: '100%', sm: 'auto' },
    flexShrink: 0,
    minWidth: 0  // Prevents overflow issues
  }}
>
  
  <IconButton 
    color="primary" 
    size="large"
    onClick={handleSubmitComment}
    disabled={!newComment.trim() || submitting}
    sx={{
      flexShrink: 0,  // Prevents the icon from shrinking
      ml: { xs: 0.5, sm: 0 }  // Small margin on mobile for spacing
    }}
  >
    {submitting ? <CircularProgress size={20} /> : <SendIcon />}
  </IconButton>
</Box>


           
           </Box>
         )}

    {visibleItems.map((comment) => (

<Box key={comment.id} mb={1} >
      <CommentItem
      comment={comment}
      currentUser={currentUser}
      onDelete={handleDeleteComment}
      onReply={handleReply}
      onWarn={handleWarnComment}
      onUnflag={handleUnflagComment}
      onRefresh={fetchComments}
      canModerate={canModerate}
      targetType={targetType}
      targetId={comment.id}
      maxDepth={2}
    />
    </Box>
    ))}
 
    
     {/* <Box
          sx={{
            mt: 0,
            pt: expanded ? 1 : 0,
            borderTop: expanded ? 1 : 0,
            borderColor: 'divider',
          }}
        >

          <Button
            size="small"
            startIcon={expanded ? <ExpandLessIcon /> : <CommentIcon />}
            onClick={() => setExpanded(!expanded)}
            sx={{ mb: expanded ? 1 : 0 }}
          >
        {expanded ? 'Hide Comments' : `Comments${comments.length > 0 ? ` (${comments.length})` : ''}`}
      </Button> 
      </Box> */}


  {collapsedItems.length > 0 && (
    <>

      <Collapse in={expanded} collapsedSize={0}>
        {/* <Box
          sx={{
            pl: 2,
            ml: 1,
            pt: 0.5, // prevents margin collapse
            borderLeft: 2,
            borderColor: 'grey.200',
          }}
        >    */}
          {error && (
            <Alert severity="error" size="small" sx={{ mb: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <CircularProgress size={20} sx={{ my: 1 }} />
          ) : (
            <>
              {collapsedItems.length === 0 ? (
                <>
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No comments yet. Be the first to comment!
                </Typography>
                </>
              ) : (
                <Stack  gap={1} >
                  {collapsedItems.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      currentUser={currentUser}
                      onDelete={handleDeleteComment}
                      onReply={handleReply}
                      onWarn={handleWarnComment}
                      onUnflag={handleUnflagComment}
                      onRefresh={fetchComments}
                      canModerate={canModerate}
                      targetType={targetType}
                      targetId={comment.id}
                      maxDepth={2}
                    />
                  ))}
                </Stack>
              )}

              {/* Add comment form */}
              
              {/* {currentUser && (
                // <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                  {/* <Avatar 
                    src={currentUser?.avatar} 
                    alt={currentUser?.first_name || 'User'}
                    sx={{ width: 32, height: 32, mr: 1.5, fontSize: '1.5rem',
                      backgroundColor: theme.palette.primary.avatar,
                      color: 'white',
                      // boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                      // border: `1px solid ${theme.palette.sidebar.active}`,
                      // mb: 0.5,
                      // mt: 0.5
                     }}
                  >
                    {currentUser.first_name?.[0]}
                  </Avatar> */}
                  {/* <TextField

                    
                    fullWidth
                    size="small"
                    placeholder="Write a comment..."
                    
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    multiline
                    maxRows={4}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                  /> 
                 </Box>
               )} */}
            </>
          )}
        {/* </Box> */}
      </Collapse>


      <Button
        size="small"
        onClick={() => setExpanded((v) => !v)}
        sx={{ mt: 1 }}
      >
        {expanded
          ? 'Show less'
          : `Show ${collapsedItems.length} more...`}
      </Button>

    </>
    
  )}



  </Box>
  
  );
}

export default Comments;